import { ColumnDef } from '@tanstack/react-table'
import { Loader } from '../../components/simple/loader'
import { SimpleLayout } from '../../layouts/simple'
import { Teacher } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../request'
import React from 'react'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { LocalTable, LocalTableRef } from '../../components/table'
import { TextInput } from '../../components/form/textInput'
import assert from 'assert'
import { Popups } from '../../providers/popups'
import { IconCaretLeft, IconCaretRight } from '../../icons/library'
import { useNavigate } from 'react-router-dom'

type ExclusionClass = {
  syncID: number | string
  name: string
  teacherName: string
  studentCount: number
  excluded: boolean
}

const sharedColumns: ColumnDef<ExclusionClass>[] = [
  {
    header: 'Section ID',
    accessorKey: 'syncID',
  },
  {
    header: 'Class Name',
    accessorKey: 'name',
  },
  {
    header: 'Teacher Name',
    accessorKey: 'teacherName',
  },
  {
    header: 'Students',
    accessorKey: 'studentCount',
  },
]

export const ExclusionClassesPage: React.FC<{ user: Teacher; id: number }> = ({ user, id }) => {
  const { data } = useRequest<{ classes: ExclusionClass[]; school: string }>(
    '/frontend_api/getConferenceExclusionClasses',
    React.useMemo(
      () => ({
        conferenceID: id,
      }),
      [id]
    )
  )

  const [excludedClasses, setExcludedClasses] = React.useState<ExclusionClass[]>([])
  const [includedClasses, setIncludedClasses] = React.useState<ExclusionClass[]>([])
  React.useEffect(() => {
    if (data) {
      setExcludedClasses(data.classes.filter((c) => c.excluded))
      setIncludedClasses(data.classes.filter((c) => !c.excluded))
    }
  }, [data])

  const excludedColumns = React.useMemo(
    () => [
      ...sharedColumns,
      {
        header: 'Include',
        cell: ({ row }) => (
          <Button
            shape={ButtonShape.iconSmall}
            buttonStyle={ButtonStyle.mint}
            onClick={() => {
              setExcludedClasses((ec) => ec.filter((c) => c.syncID !== row.original.syncID))
              setIncludedClasses((ic) => [...ic, row.original])
              setUnsavedChanges(true)
            }}
          >
            <IconCaretLeft />
          </Button>
        ),
      },
    ],
    [setExcludedClasses, setIncludedClasses]
  )
  const includedColumns = React.useMemo(
    () => [
      ...sharedColumns,
      {
        header: 'Exclude',
        cell: ({ row }) => (
          <Button
            shape={ButtonShape.iconSmall}
            buttonStyle={ButtonStyle.mint}
            onClick={() => {
              setIncludedClasses((ic) => ic.filter((c) => c.syncID !== row.original.syncID))
              setExcludedClasses((ec) => [...ec, row.original])
              setUnsavedChanges(true)
            }}
          >
            <IconCaretRight />
          </Button>
        ),
      },
    ],
    [setExcludedClasses, setIncludedClasses]
  )

  const [includedSearch, setIncludedSearch] = React.useState('')
  const [excludedSearch, setExcludedSearch] = React.useState('')

  const inclusionsTable = React.useRef<LocalTableRef<ExclusionClass>>(null)
  const exclusionsTable = React.useRef<LocalTableRef<ExclusionClass>>(null)

  assert(
    includedClasses.length + excludedClasses.length === data?.classes.length ||
      includedClasses.length + excludedClasses.length === 0,
    'Invalid class count'
  )

  const [unsavedChanges, setUnsavedChanges] = React.useState(false)
  const warnAboutUnsaved = async (callback: () => void) => {
    if (
      !unsavedChanges ||
      (await Popups.simpleConfirm(
        <b>You have unsaved changes.</b>,
        'Are you sure you want to leave? To save before continuing, press the button at the bottom of the page.'
      ))
    )
      callback()
  }

  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)
  const save = () => {
    setLoading(true)
    makeRequest('/frontend_api/updateConferenceExclusionClasses', {
      conferenceID: id,
      excludedSyncIDs: excludedClasses.map((c) => c.syncID),
    })
      .then(() => navigate(`/ptc/adminConferenceVerification/verifyInvites/${id}`))
      .finally(() => setLoading(false))
      .catch(showAxiosErrorAsToast)
  }

  return (
    <SimpleLayout>
      {data ? (
        <div
          className={`my-4 flex flex-col items-center gap-2 self-center ${loading ? 'pointer-events-none opacity-50' : ''}`}
        >
          <div className="text-2xl font-bold">Exclude Classes From Conferences At {data.school}</div>
          <div className="max-w-[50%]">
            When inviting families to a conference as an admin, each family wil be invited to meet with the teacher of
            every class their students are in. Here, you can choose classes to ignore when inviting families to this
            conference. In the next step, you can review and confirm how many invites each family and teacher will
            receive.
          </div>
          <div className={`my-4 flex w-screen justify-center px-10`}>
            <div className="flex basis-1/2 flex-col items-center gap-2 pr-2">
              <div className="text-lg font-bold">Included Classes ({includedClasses.length})</div>
              <div className="flex items-center gap-2">
                <TextInput value={includedSearch} onChange={setIncludedSearch} placeholder="Search..." />
                <Button
                  buttonStyle={ButtonStyle.mint}
                  onClick={async () => {
                    const filtered = inclusionsTable.current?.filteredRows || []
                    if (filtered.length === 0) return
                    if (!(await Popups.simpleConfirm(`Are you sure you want to exclude ${filtered.length} classes?`)))
                      return
                    const removedSyncIDs = new Set(filtered.map((r) => r.syncID))
                    setIncludedClasses((ic) => ic.filter((c) => !removedSyncIDs.has(c.syncID)))
                    setExcludedClasses((ec) => ec.concat(includedClasses.filter((c) => removedSyncIDs.has(c.syncID))))
                    setUnsavedChanges(true)
                  }}
                >
                  Exclude all
                  {includedSearch.length > 0 ? ` matching "${includedSearch}"` : ''}
                  <IconCaretRight />
                </Button>
              </div>
              <LocalTable
                saveLocationOnDataChange
                className="w-full"
                ref={inclusionsTable}
                search={includedSearch}
                columns={includedColumns}
                data={includedClasses}
                sortable
                pagination={10}
              />
            </div>
            <div className="my-8 w-px justify-self-stretch bg-gray-200" />
            <div className="flex basis-1/2 flex-col items-center gap-2 pl-2">
              <div className="text-lg font-bold">Excluded Classes ({excludedClasses.length})</div>
              <div className="flex items-center gap-2">
                <TextInput value={excludedSearch} onChange={setExcludedSearch} placeholder="Search..." />
                <Button
                  buttonStyle={ButtonStyle.mint}
                  onClick={async () => {
                    const filtered = exclusionsTable.current?.filteredRows || []
                    if (filtered.length === 0) return
                    if (!(await Popups.simpleConfirm(`Are you sure you want to include ${filtered.length} classes?`)))
                      return
                    const removedSyncIDs = new Set(filtered.map((r) => r.syncID) || [])
                    setExcludedClasses((ec) => ec.filter((c) => !removedSyncIDs.has(c.syncID)))
                    setIncludedClasses((ic) => ic.concat(excludedClasses.filter((c) => removedSyncIDs.has(c.syncID))))
                    setUnsavedChanges(true)
                  }}
                >
                  <IconCaretLeft />
                  Include all
                  {excludedSearch.length > 0 ? ` matching "${excludedSearch}"` : ''}
                </Button>
              </div>
              <LocalTable
                saveLocationOnDataChange
                className="w-full"
                ref={exclusionsTable}
                search={excludedSearch}
                columns={excludedColumns}
                data={excludedClasses}
                sortable
                pagination={10}
              />
            </div>
          </div>
          <div className="flex items-center gap-8">
            <Button onClick={() => warnAboutUnsaved(() => navigate(`/ptc/conference/${id}#invite`))}>
              <IconCaretLeft /> Back
            </Button>
            <Button logo onClick={save}>
              Save and Review
            </Button>
            <Button
              onClick={() => warnAboutUnsaved(() => navigate(`/ptc/adminConferenceVerification/verifyInvites/${id}`))}
            >
              Skip to Verify Invites <IconCaretRight />
            </Button>
          </div>
        </div>
      ) : (
        <Loader className="mt-8" />
      )}
    </SimpleLayout>
  )
}
